// OwnFundingCard — figure S4b's left-rail card:
//
//   💰 Duplicate funding asks
//   Where two inventions in one group would each ask the same funding body,
//   file once instead — start from the strongest group.
//
// The card is ALWAYS rendered, never hidden behind a group selection and never
// hidden when there is nothing to show: a heading that disappears when a
// component is behaving correctly tells the reader nothing.
//
// PRIVACY. At own scope no funding surface may resolve a person for a project
// whose owner is not the caller. This component renders NOTHING about people at
// all: not an owner id, not a name, not an email. That part is unconditional.
//
// The breach warning below is deliberately narrow, and LIVE DATA is why:
// GET /whitespace/portfolio_overlap/funding?scope=own really does return
// mention rows carrying owner_id, and that owner_id is the CALLER'S OWN sub on
// the caller's own project. An own row is not a leak. The warning fires only
// for an owner_id belonging to someone OTHER than the caller, which is the case
// the story actually forbids; with no caller identity supplied it stays silent
// rather than claiming a breach it cannot establish.
import { useMemo } from 'react'
import { duplicateFundingRows } from './ownOverlapText'
import type { OverlapFundingRollup } from '../../types/whitespace'

export interface OwnFundingCardProps {
  /** GET /whitespace/portfolio_overlap/funding at own scope. */
  rollup?: OverlapFundingRollup
  /** The signed-in caller's own Cognito sub. Used ONLY to tell an own row from
   * someone else's. Never rendered. */
  callerUserId?: string
}

export default function OwnFundingCard({ rollup, callerUserId }: OwnFundingCardProps) {
  const duplicateRows = useMemo(() => duplicateFundingRows(rollup), [rollup])

  const foreignOwnerIdPresent = useMemo(
    () =>
      callerUserId !== undefined &&
      (rollup?.funding_mentions_rows ?? []).some((r) =>
        r.mentions.some((m) => m.owner_id != null && m.owner_id !== callerUserId),
      ),
    [rollup, callerUserId],
  )

  return (
    <section data-part="OwnFundingCard" className="rounded-xl border border-gray-200 bg-white px-3.5 py-3">
      <h4 className="text-[13px] font-bold text-gray-900">💰 Duplicate funding asks</h4>
      <p className="mt-0.5 text-[11.5px] leading-relaxed text-gray-500">
        Where two inventions in one group would each ask the same funding body, file once instead —
        start from the strongest group.
      </p>

      <p className="mt-1.5 text-[11.5px] leading-relaxed text-gray-600">
        {rollup === undefined
          ? 'Your funding rollup has not come back, so no repeated ask has been found — and none has been ruled out either.'
          : duplicateRows.length === 0
            ? 'No funding body is named by more than one of your inventions, so there is nothing to file once instead of twice.'
            : duplicateRows
                .map((r) => {
                  const projectCount = new Set(r.mentions.map((m) => m.project_id)).size
                  const programme = [r.programme, r.identifier].filter(Boolean).join(' ') || 'One funding body'
                  return `${programme} — named by ${projectCount} of your inventions`
                })
                .join(' · ')}
      </p>

      {foreignOwnerIdPresent && (
        <p role="alert" className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
          These funding rows name someone other than you as an owner. Nothing about that person is
          rendered here, but at your own scope that field should not have been sent at all — this is
          a backend contract breach, not something this screen should absorb.
        </p>
      )}
    </section>
  )
}
